import type { LucideIcon } from "lucide-react";
import {
  CheckCheck,
  CircleX,
  Clock,
  Eye,
  Send,
  Users,
} from "lucide-react";
import type {
  MessageOriginName,
  MessageStatusName,
  PlatformTypeName,
  ReportMessageListItem,
  ReportMessageStatusBucketName,
  ReportMessageSummaryResponse,
} from "@multi-chat/shared";
import {
  contactsTableBodyCellClass,
  contactsTableBodyRowClass,
  contactsTableClass,
  contactsTableHeadRowClass,
  contactsTableHeaderCellClass,
  contactsTableHeaderIconWrapClass,
  contactsTableHeaderLabelClass,
  contactsTableShellClass,
  contactsValueChipClass,
} from "../contacts/contacts-table-presentation.js";

export {
  contactsTableBodyCellClass,
  contactsTableBodyRowClass,
  contactsTableClass,
  contactsTableHeadRowClass,
  contactsTableHeaderCellClass,
  contactsTableHeaderIconWrapClass,
  contactsTableHeaderLabelClass,
  contactsTableShellClass,
  contactsValueChipClass,
};

export type OutgoingReportMetricKey = keyof Pick<
  ReportMessageSummaryResponse,
  "total" | "processing" | "sent" | "delivered" | "read" | "failed"
>;

export type OutgoingReportMetricTone =
  | "neutral"
  | "accent"
  | "success"
  | "read"
  | "error";

export type OutgoingReportMetricCard = {
  key: OutgoingReportMetricKey;
  /** null clears statusBucket (Total). */
  statusBucket: ReportMessageStatusBucketName | null;
  labelKey: string;
  icon: LucideIcon;
  tone: OutgoingReportMetricTone;
};

export const OUTGOING_REPORT_METRIC_CARDS: readonly OutgoingReportMetricCard[] = [
  {
    key: "total",
    statusBucket: null,
    labelKey: "reports.outgoing.metrics.total",
    icon: Users,
    tone: "neutral",
  },
  {
    key: "processing",
    statusBucket: "PROCESSING",
    labelKey: "reports.outgoing.metrics.processing",
    icon: Clock,
    tone: "accent",
  },
  {
    key: "sent",
    statusBucket: "SENT",
    labelKey: "reports.outgoing.metrics.sent",
    icon: Send,
    tone: "accent",
  },
  {
    key: "delivered",
    statusBucket: "DELIVERED",
    labelKey: "reports.outgoing.metrics.delivered",
    icon: CheckCheck,
    tone: "success",
  },
  {
    key: "read",
    statusBucket: "READ",
    labelKey: "reports.outgoing.metrics.read",
    icon: Eye,
    tone: "read",
  },
  {
    key: "failed",
    statusBucket: "FAILED",
    labelKey: "reports.outgoing.metrics.failed",
    icon: CircleX,
    tone: "error",
  },
];

export function outgoingReportMetricToneClass(tone: OutgoingReportMetricTone): string {
  switch (tone) {
    case "neutral":
      return "border-line/70 bg-surface-muted/70 text-muted";
    case "accent":
      return "border-accent/20 bg-accent/10 text-accent";
    case "success":
      return "border-success/25 bg-success-muted text-success";
    case "read":
      return "border-success/35 bg-success-muted text-success";
    case "error":
      return "border-error/25 bg-error-muted text-error";
    default: {
      const _exhaustive: never = tone;
      return _exhaustive;
    }
  }
}

export function outgoingReportStatusChipClass(
  status: MessageStatusName | ReportMessageStatusBucketName,
): string {
  switch (status) {
    case "READ":
      return "border-success/35 bg-success-muted text-success";
    case "DELIVERED":
      return "border-success/25 bg-success-muted text-success";
    case "SENT":
      return "border-accent/30 bg-accent/10 text-accent";
    case "FAILED":
      return "border-error/30 bg-error-muted text-error";
    case "PROCESSING":
    case "PENDING":
    case "SENDING":
    case "RECEIVED":
      return "border-accent/20 bg-accent/8 text-accent";
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
}

export function platformLabelKey(platform: PlatformTypeName): string {
  switch (platform) {
    case "WHATSAPP":
      return "reports.outgoing.platforms.WHATSAPP";
    case "FACEBOOK":
      return "reports.outgoing.platforms.FACEBOOK";
    case "INSTAGRAM":
      return "reports.outgoing.platforms.INSTAGRAM";
    default: {
      const _exhaustive: never = platform;
      return _exhaustive;
    }
  }
}

export function originLabelKey(origin: MessageOriginName): string {
  switch (origin) {
    case "INBOX":
      return "reports.outgoing.origins.INBOX";
    case "CAMPAIGN":
      return "reports.outgoing.origins.CAMPAIGN";
    case "CAMPAIGN_SEND_TEST":
      return "reports.outgoing.origins.CAMPAIGN_SEND_TEST";
    case "SYSTEM":
      return "reports.outgoing.origins.SYSTEM";
    case "AI_AGENT":
      return "reports.outgoing.origins.AI_AGENT";
    case "AUTOMATION":
      return "reports.outgoing.origins.AUTOMATION";
    case "API":
      return "reports.outgoing.origins.API";
    case "MOBILE_APP":
      return "reports.outgoing.origins.MOBILE_APP";
    default: {
      const _exhaustive: never = origin;
      return _exhaustive;
    }
  }
}

export function statusBucketLabelKey(bucket: ReportMessageStatusBucketName): string {
  switch (bucket) {
    case "PROCESSING":
      return "reports.outgoing.statusBuckets.PROCESSING";
    case "SENT":
      return "reports.outgoing.statusBuckets.SENT";
    case "DELIVERED":
      return "reports.outgoing.statusBuckets.DELIVERED";
    case "READ":
      return "reports.outgoing.statusBuckets.READ";
    case "FAILED":
      return "reports.outgoing.statusBuckets.FAILED";
    default: {
      const _exhaustive: never = bucket;
      return _exhaustive;
    }
  }
}

export function statusLabelKey(status: MessageStatusName): string {
  switch (status) {
    case "RECEIVED":
      return "reports.outgoing.statuses.RECEIVED";
    case "PENDING":
      return "reports.outgoing.statuses.PENDING";
    case "SENDING":
      return "reports.outgoing.statuses.SENDING";
    case "SENT":
      return "reports.outgoing.statuses.SENT";
    case "DELIVERED":
      return "reports.outgoing.statuses.DELIVERED";
    case "READ":
      return "reports.outgoing.statuses.READ";
    case "FAILED":
      return "reports.outgoing.statuses.FAILED";
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
}

export function originBadgeLabel(
  t: (key: string, options?: Record<string, unknown>) => string,
  origin: MessageOriginName | null,
): string {
  if (origin == null) return "—";
  const key = originLabelKey(origin);
  const translated = t(key);
  return translated === key ? t("reports.outgoing.origins.unknown", { origin }) : translated;
}

export function recipientDisplayPrimary(contact: ReportMessageListItem["contact"]): string {
  const name = contact.displayName?.trim() || contact.platformDisplayName?.trim() || "";
  if (name.length > 0) return name;
  const phone = contact.normalizedPhoneNumber?.trim() || "";
  if (phone.length > 0) return phone;
  const external = contact.externalProfileId?.trim() || "";
  return external.length > 0 ? external : "—";
}

export function recipientDisplaySecondary(
  contact: ReportMessageListItem["contact"],
): string | null {
  const primary = recipientDisplayPrimary(contact);
  const phone = contact.normalizedPhoneNumber?.trim() || "";
  if (phone.length > 0 && phone !== primary) return phone;
  const username = contact.platformUsername?.trim() || "";
  if (username.length > 0 && username !== primary) {
    return username.startsWith("@") ? username : `@${username}`;
  }
  const external = contact.externalProfileId?.trim() || "";
  if (external.length > 0 && external !== primary) return external;
  return null;
}

export function formatReportDateTime(iso: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(iso));
}

export function messagePreviewOrTemplate(row: ReportMessageListItem): string {
  if (row.template?.name) {
    const lang = row.template.languageCode?.trim();
    return lang ? `${row.template.name} (${lang})` : row.template.name;
  }
  const preview = row.preview?.trim() ?? "";
  return preview.length > 0 ? preview : "—";
}

export function showingRangeBounds(input: {
  page: number;
  pageSize: number;
  count: number;
}): { start: number; end: number } {
  if (input.count === 0) return { start: 0, end: 0 };
  const start = (input.page - 1) * input.pageSize + 1;
  return { start, end: start + input.count - 1 };
}
